import { useIsFocused } from 'expo-router'
import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { AccessibilityInfo, ScrollView, View } from 'react-native'
import Animated, { ReduceMotion, SlideInRight, SlideOutLeft, useReducedMotion } from 'react-native-reanimated'

import { Button } from '@/src/elements/button/default/Button'
import { Modal } from '@/src/elements/modal/Modal'
import { BarStepper } from '@/src/elements/stepper/bar/BarStepper'
import {
  hasSeenNewPrayerSpaceOnboarding,
  markNewPrayerSpaceOnboardingAsSeen,
} from '@/src/features/prayer-space/services/newPrayerSpaceOnboardingStorage'
import { StepFour } from '@/src/features/prayer-space/ui/screens/PrayerSpaceScreenV2/NewPrayerSpaceOnboardingSteps/StepFour'
import { StepOne } from '@/src/features/prayer-space/ui/screens/PrayerSpaceScreenV2/NewPrayerSpaceOnboardingSteps/StepOne'
import { StepThree } from '@/src/features/prayer-space/ui/screens/PrayerSpaceScreenV2/NewPrayerSpaceOnboardingSteps/StepThree'
import { StepTwo } from '@/src/features/prayer-space/ui/screens/PrayerSpaceScreenV2/NewPrayerSpaceOnboardingSteps/StepTwo'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { useTimeout } from '@/src/general/hooks/useTimeout'
import { GTM } from '@/src/general/utils/gtm'
import { EVENTS } from '@/src/general/utils/gtm/constants/Events.enum'
import { useTranslate } from '@/src/lang/useTranslate'

type TNewPrayerSpaceModalProps = {
  userId: number
}

const STEPS = [StepOne, StepTwo, StepThree, StepFour]

const CLOSED_EVENTS = [
  EVENTS.PRAYER_SPACE_ONBOARDING_CLOSED_STEP_1,
  EVENTS.PRAYER_SPACE_ONBOARDING_CLOSED_STEP_2,
  EVENTS.PRAYER_SPACE_ONBOARDING_CLOSED_STEP_3,
  EVENTS.PRAYER_SPACE_ONBOARDING_CLOSED_STEP_4,
] as const

const STEP_TITLE_KEYS = [
  'prayer-space.onboarding.step-1.title',
  'prayer-space.onboarding.step-2.title',
  'prayer-space.onboarding.step-3.title',
  'prayer-space.onboarding.step-4.title',
] as const

const ANIMATION_DURATION = 300

const enteringAnimation = SlideInRight.duration(ANIMATION_DURATION).reduceMotion(ReduceMotion.System)
const exitingAnimation = SlideOutLeft.duration(ANIMATION_DURATION).reduceMotion(ReduceMotion.System)

export const NewPrayerSpaceModal = ({ userId }: TNewPrayerSpaceModalProps) => {
  const [showModal, setShowModal] = useState(false)
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const isClosing = useRef(false)
  const hasOpened = useRef(false)
  const intl = useIntl()
  const { t } = useTranslate()
  const isFocused = useIsFocused()
  const shouldReduceMotion = useReducedMotion()
  const Step = STEPS[currentStepIndex]
  const isLastStep = currentStepIndex === STEPS.length - 1
  const hasSeenOnboarding = hasSeenNewPrayerSpaceOnboarding(userId)

  const { lessThan } = useDimensions()
  useTimeout(
    () => {
      if (hasOpened.current) return
      hasOpened.current = true
      setShowModal(true)
      GTM.sendEvent(EVENTS.PRAYER_SPACE_ONBOARDING_DISPLAY)
    },
    hasSeenOnboarding || !isFocused ? null : 1000,
  )
  useTimeout(() => setIsTransitioning(false), isTransitioning ? ANIMATION_DURATION : null)

  useEffect(() => {
    if (!showModal) return
    AccessibilityInfo.announceForAccessibility(intl.formatMessage({ id: STEP_TITLE_KEYS[currentStepIndex] }))
  }, [currentStepIndex, intl, showModal])

  const handleClose = () => {
    if (isClosing.current) return
    isClosing.current = true
    markNewPrayerSpaceOnboardingAsSeen(userId)
    GTM.sendEvent(CLOSED_EVENTS[currentStepIndex])
    setShowModal(false)
  }

  const handleNext = () => {
    if (isTransitioning) return
    if (isLastStep) {
      handleClose()
      return
    }

    if (!shouldReduceMotion) setIsTransitioning(true)
    setCurrentStepIndex((value) => value + 1)
  }

  return (
    <Modal
      showModal={showModal}
      onCancel={handleClose}
      fullWidth="mobile"
      style={!lessThan.md ? { maxWidth: 500, marginRight: 'auto', marginLeft: 'auto' } : null}
      classNames={{
        base: 'h-[560px] max-h-[90%] bg-primary-bg-medium md:max-w-[500px] px-0',
        body: 'flex-1 py-0 px-0 overflow-visible',
        header: 'p-0 md:min-h-10',
        handleContainer: 'bg-primary-bg-medium',
      }}
    >
      <View className="flex-1 z-20">
        <BarStepper className="px-8 mt-4" totalSteps={STEPS.length} currentStep={currentStepIndex + 1} />
        <Animated.View
          key={currentStepIndex}
          className="flex-1 -z-10"
          entering={showModal && currentStepIndex > 0 ? enteringAnimation : undefined}
          exiting={showModal ? exitingAnimation : undefined}
        >
          <ScrollView
            testID={TEST_ID.PRAYER_SPACE.ONBOARDING_STEP}
            className="flex-1 overflow-visible"
            contentContainerClassName="grow justify-center py-5"
            showsVerticalScrollIndicator={false}
          >
            <Step />
          </ScrollView>
        </Animated.View>

        <Button
          testID={TEST_ID.MODAL.CONFIRM_BUTTON}
          onPress={handleNext}
          isDisabled={isTransitioning}
          size="md"
          variant={isLastStep ? 'solid' : 'light'}
          colors="primary"
          endIcon={isLastStep ? undefined : ICON.ARROW_RIGHT}
          className="ml-auto mt-4 mr-4"
        >
          {t(isLastStep ? 'tab.discover' : 'word.next')}
        </Button>
      </View>
    </Modal>
  )
}
